<template>
  <div class="customer-data-box">
    <InfoautoEditCustomerModal
      v-if="user && showEditModal"
      :customer="user"
      @customerUpdated="loadCustomer"
      @close="showEditModal = false"
    />
    <ManageResaleTaxModal
      v-if="showManageResaleTax && user"
      :customerEmail="user.generalInfo.email"
      :customerName="user.generalInfo.name"
      :customerInfoautoId="customerId"
      @close="showManageResaleTax = false"
      @resaleTaxCreated="resaleTaxCreated"
    />

    <!-- Loading -->
    <div v-if="loading" class="card-loading">
      <baseMiniSpinner />
    </div>

    <template v-if="user">
      <!-- Customer Data Card -->
      <div class="nav data-card">
        <div class="card-header-row">
          <h2>{{ $t("message.customer_modal.customer_data") }}</h2>
          <button
            v-if="userCanEditInfoautoCustomer"
            @click="showEditModal = true"
            class="edit-btn"
            title="Edit"
          >
            <img src="@/assets/icons/edit.svg" alt="Edit" />
          </button>
        </div>

        <table class="data-table">
          <tbody>
            <tr>
              <td class="data-label">ID</td>
              <td class="data-value">{{ user.generalInfo.infoautoId }}</td>
            </tr>
            <tr>
              <td class="data-label">
                {{ $t("message.customers_page.name") }}
              </td>
              <td class="data-value">
                {{ user.generalInfo.name }} {{ user.generalInfo.surname }}
              </td>
            </tr>
            <tr>
              <td class="data-label">
                {{ $t("message.customer_modal.company_name") }}
              </td>
              <td class="data-value">
                {{ user.generalInfo.companyName || "—" }}
              </td>
            </tr>
            <tr>
              <td class="data-label">
                {{ $t("message.customers_page.email") }}
              </td>
              <td class="data-value">{{ user.generalInfo.email }}</td>
            </tr>
            <tr v-if="user.generalInfo.email2">
              <td class="data-label">
                {{ $t("message.customer_modal.email2") }}
              </td>
              <td class="data-value">{{ user.generalInfo.email2 }}</td>
            </tr>
            <tr>
              <td class="data-label">
                {{ $t("message.customer_modal.price_level") }}
              </td>
              <td class="data-value">
                {{ user.generalInfo.priceLevel }}
                <span
                  v-if="user.generalInfo.priceLevelInfoauto?.descripcion"
                  class="text-gray-500"
                >
                  ({{ user.generalInfo.priceLevelInfoauto.descripcion }})
                </span>
              </td>
            </tr>
            <tr>
              <td class="data-label">
                {{ $t("message.customer_modal.modalidad_venta") }}
              </td>
              <td class="data-value">
                {{
                  decodedModalidadVta
                    ? `${decodedModalidadVta.codigo}: ${decodedModalidadVta.description}`
                    : "—"
                }}
              </td>
            </tr>
            <tr>
              <td class="data-label">
                {{ $t("message.customer_modal.seller") }}
              </td>
              <td class="data-value">{{ seller || "—" }}</td>
            </tr>
            <tr>
              <td class="data-label">{{ $t("message.customer_modal.tax") }}</td>
              <td class="data-value">{{ user.generalInfo.tax }}</td>
            </tr>
            <tr v-if="user.generalInfo.notes">
              <td class="data-label">
                {{ $t("message.customer_modal.notes") }}
              </td>
              <td class="data-value">{{ user.generalInfo.notes }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Tax Status Banner -->
        <div
          class="tax-banner"
          :class="customerTaxable ? 'taxable' : 'not-taxable'"
        >
          <div class="tax-status">
            <span v-if="customerTaxable">{{
              $t("message.customer_modal.customer_taxable")
            }}</span>
            <span v-else>{{
              $t("message.customer_modal.customer_not_taxable")
            }}</span>
            <span
              class="tax-badge"
              :class="user.validResaleTax.length > 0 ? 'valid' : 'invalid'"
            >
              {{
                user.validResaleTax.length > 0
                  ? $t("message.customer_modal.valid_resale_tax")
                  : $t("message.customer_modal.no_valid_resale_tax")
              }}
            </span>
          </div>
          <button class="manage-tax-btn" @click="showManageResaleTax = true">
            {{ $t("message.customer_modal.manage_resale_tax") }}
          </button>
        </div>
      </div>

      <!-- Credit Card -->
      <div class="nav credit-card">
        <h2>{{ $t("message.customer_modal.credit") }}</h2>
        <div class="credit-grid">
          <div class="credit-item">
            <span class="credit-label">{{
              $t("message.customer_modal.credit_limit")
            }}</span>
            <span class="credit-value">{{
              user.credit.mtoLimCia | currencyFormatted(true)
            }}</span>
          </div>
          <div class="credit-item">
            <span class="credit-label">{{
              $t("message.customer_modal.available_credit")
            }}</span>
            <span class="credit-value available">{{
              user.credit.mtoDispCia | currencyFormatted(true)
            }}</span>
          </div>
          <div class="credit-item">
            <span class="credit-label">{{
              $t("message.customer_modal.used_credit")
            }}</span>
            <span class="credit-value used">{{
              user.credit.mtoUsaCia | currencyFormatted(true)
            }}</span>
          </div>
          <div class="credit-item" v-if="user.credit.mtoVencCia > 0">
            <span class="credit-label">{{
              $t("message.customer_modal.overdue_credit")
            }}</span>
            <span class="credit-value overdue">{{
              user.credit.mtoVencCia | currencyFormatted(true)
            }}</span>
          </div>
        </div>
        <div class="credit-details">
          <div class="data-row">
            <span class="data-label">{{
              $t("message.customer_modal.credit_blocked")
            }}</span>
            <span
              class="data-value"
              :class="{
                'text-red-600 font-semibold': user.credit.bloqVtaCia != 0
              }"
            >
              {{
                user.credit.bloqVtaCia == 0
                  ? $t("message.customer_modal.no")
                  : $t("message.customer_modal.yes")
              }}
            </span>
          </div>
          <div class="data-row">
            <span class="data-label">{{
              $t("message.customer_modal.credit_days")
            }}</span>
            <span class="data-value"
              >{{ user.credit.diasCredito }}
              {{ $t("message.customer_modal.days") }}</span
            >
          </div>
          <div class="data-row">
            <span class="data-label">{{
              $t("message.customer_modal.expiration_days")
            }}</span>
            <span class="data-value"
              >{{ user.credit.diasVenc }}
              {{ $t("message.customer_modal.days") }}</span
            >
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import InfoautoEditCustomerModal from "../customer/InfoautoEditCustomerModal.vue";
import { infoautoGetSingleCustomer } from "@/api/store/Infoauto.js";
import ManageResaleTaxModal from "@/components/customer/ManageResaleTaxModal";
export default {
  components: {
    InfoautoEditCustomerModal,
    ManageResaleTaxModal
  },
  props: {
    customerId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showEditModal: false,
      user: null,
      loading: true,
      error: false,
      showManageResaleTax: false
    };
  },
  computed: {
    customerTaxable() {
      if (!this.user) return false;
      if (this.user.validResaleTax.length > 0) {
        return false;
      }
      if (this.user.generalInfo.tax == "0") {
        return false;
      }

      return true;
    },
    seller() {
      if (!this.user || !this.$store.getters["general/getSellers"]) return null;
      return this.$store.getters["general/getSellers"].find(
        seller => seller.id == this.user.generalInfo.vendedorId
      )?.name;
    },
    userCanEditInfoautoCustomer() {
      return this.$store.getters["auth/userHasPermission"](
        "can_edit_infoauto_customer"
      );
    },
    modalidadesVenta() {
      return this.$store.getters["general/getModalidadesVenta"];
    },
    decodedModalidadVta() {
      return this.modalidadesVenta.find(
        modalidad =>
          modalidad.modalidadVtaId == this.user.generalInfo.modalidadVtaId
      );
    }
  },
  async mounted() {
    try {
      this.loadCustomer();
    } catch (error) {
      this.error = true;
    }
    this.loading = false;
  },
  methods: {
    resaleTaxCreated() {
      this.showManageResaleTax = false;
      this.loadCustomer();
    },
    async loadCustomer() {
      this.user = await infoautoGetSingleCustomer(this.customerId);
    }
  }
};
</script>
<style lang="scss" scoped>
.customer-data-box {
  display: contents;
  font-size: 14px;
  color: #1a1a2e;
}

.card-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  width: 100%;
}

.data-card,
.credit-card {
  @apply p-4 border rounded-md;
  width: 100%;
  h2 {
    font-weight: 700;
    font-size: 16px;
    margin: 0 0 12px 0;
    color: #111827;
  }
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.edit-btn {
  width: 32px;
  height: 32px;
  padding: 6px;
  background: #3b82f6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  img {
    width: 100%;
    filter: brightness(0) invert(1);
  }
  &:hover {
    background: #2563eb;
  }
}

/* Data table */
.data-table {
  width: 100%;
  border-collapse: collapse;
  td {
    padding: 5px 8px;
    vertical-align: top;
    line-height: 1.5;
  }
  tr:nth-child(even) {
    background: #f9fafb;
  }
  tr:hover {
    background: #f3f4f6;
  }
}
.data-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  width: 1%;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.data-value {
  font-size: 13px;
  color: #1f2937;
  word-break: break-word;
}

/* En móvil apilamos etiqueta sobre valor para evitar que la columna
   de la etiqueta se colapse y el texto se parta letra por letra */
@media (max-width: 640px) {
  .data-table,
  .data-table tbody,
  .data-table tr,
  .data-table td {
    display: block;
    width: 100%;
  }
  .data-table tr {
    padding: 8px 0;
    border-bottom: 1px solid #f3f4f6;
  }
  .data-table td {
    padding: 0;
    white-space: normal;
  }
  .data-label {
    width: auto;
    white-space: normal;
    margin-bottom: 2px;
  }
}

/* Tax Banner */
.tax-banner {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  &.taxable {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }
  &.not-taxable {
    background: #fef2f2;
    border: 1px solid #fecaca;
  }
}
.tax-status {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  font-weight: 500;
}
.tax-badge {
  font-size: 11px;
  font-weight: 600;
  &.valid {
    color: #059669;
  }
  &.invalid {
    color: #dc2626;
  }
}
.manage-tax-btn {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 5px;
  background: #374151;
  color: white;
  white-space: nowrap;
  &:hover {
    background: #1f2937;
  }
}

/* Credit Card */
.credit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}
.credit-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.credit-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  font-weight: 600;
}
.credit-value {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  &.available {
    color: #059669;
  }
  &.used {
    color: #dc2626;
  }
  &.overdue {
    color: #fff;
    background: #dc2626;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 16px;
  }
}

.credit-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-top: 1px solid #e5e7eb;
  padding-top: 10px;
}
</style>
